import { BtnClasses, Button, CustomInput } from '@tcweb/connect-ui';
import {
  add,
  magnitude,
  normalize,
  scale as scaleVector,
  vector3,
  Vector3,
} from '@fulcrum/lib';
import { t } from '@trimble/i18n';
import {
  convertConnectModelAlignmentToViewerEuler,
  convertViewerEulerToConnectModelAlignmentAxes,
} from '@trimble/web3d-providers';
import {
  formatModelPositionAngle,
  formatModelPositionLength,
  getVectorAxis,
  areAlignmentsEqual,
  createZeroVector,
  parseModelPositionAngle,
  parseModelPositionLength,
  setVectorAxis,
} from './position-settings-helper';
import {
  Axis,
  DEFAULT_FORM_STATE,
  DEFAULT_IDENTITY_MODEL_ALIGNMENT,
  PERCENT_FACTOR,
  PositionFormState,
} from './position-settings.models';
import { FormattingSettings } from '@trimble/types';
import { Component, createEffect, createSignal, on } from 'solid-js';
import { createStore, SetStoreFunction } from 'solid-js/store';
import { TcModelAlignment } from '@data-access/connect/tc-tcps';

const PositionInput: Component<{
  name: string;
  label: string;
  value: string;
  onInput(value: string): void;
  onCommit(): void;
}> = (props) => (
  <CustomInput
    name={props.name}
    label={props.label}
    class="flex-1 [&>label]:text-gray-6"
    value={props.value}
    onInput={(event) =>
      props.onInput((event.currentTarget as HTMLInputElement).value)
    }
    onBlur={props.onCommit}
    onKeyDown={(event) => {
      if (event.key === 'Enter') {
        props.onCommit();
      }
    }}
  />
);

export interface PositionSettingsProps {
  alignment: TcModelAlignment;
  formattingSettings: FormattingSettings;
  isSaving?: boolean;
  onApplyAlignment?(alignment: TcModelAlignment): void;
  onCancel(): void;
  onHasUnsavedChanges?(hasUnsavedChanges: boolean): void;
  onSave(alignment: TcModelAlignment): Promise<void>;
}

export const PositionSettings: Component<PositionSettingsProps> = (props) => {
  const [state, setState] = createStore<PositionFormState>(
    structuredClone(DEFAULT_FORM_STATE)
  );
  const [initialAlignment, setInitialAlignment] =
    createSignal<TcModelAlignment>(
      structuredClone(DEFAULT_IDENTITY_MODEL_ALIGNMENT)
    );

  createEffect(() => {
    const current = state.alignment;
    const isEqual = areAlignmentsEqual(initialAlignment(), current);
    props.onHasUnsavedChanges?.(!isEqual);
  });

  const notifyAlignmentApplied = (): void => {
    props.onApplyAlignment?.({ ...state.alignment });
  };

  const setVectorDraft = (
    setState: SetStoreFunction<PositionFormState>,
    section: 'position' | 'rotation' | 'move',
    values: Vector3,
    format: (value: number) => string
  ): void => {
    setState('draft', section, {
      x: format(values.x),
      y: format(values.y),
      z: format(values.z),
    });
  };

  const commitPosition = (nextPosition: Vector3): void => {
    setVectorDraft(setState, 'position', nextPosition, (value) =>
      formatModelPositionLength(value, props.formattingSettings)
    );
    setState('alignment', {
      locationX: nextPosition.x,
      locationY: nextPosition.y,
      locationZ: nextPosition.z,
    });
  };

  const applyMove = (nextMove: Vector3): void => {
    setState('move', nextMove);
    setVectorDraft(setState, 'move', nextMove, (value) =>
      formatModelPositionLength(value, props.formattingSettings)
    );
    setState(
      'draft',
      'move',
      'distance',
      formatModelPositionLength(magnitude(nextMove), props.formattingSettings)
    );
    commitPosition(add(state.initialPosition, nextMove));
  };

  const applyRotation = (
    angleXInRadians: number,
    angleYInRadians: number,
    angleZInRadians: number
  ): void => {
    const nextRotation = vector3(
      angleXInRadians,
      angleYInRadians,
      angleZInRadians
    );
    setState('rotation', nextRotation);
    setVectorDraft(setState, 'rotation', nextRotation, (value) =>
      formatModelPositionAngle(value, props.formattingSettings)
    );
    setState(
      'alignment',
      convertViewerEulerToConnectModelAlignmentAxes(nextRotation)
    );
  };

  const commitPositionAxis = (axis: Axis): void => {
    const alignment = state.alignment;
    const current = vector3(
      alignment.locationX,
      alignment.locationY,
      alignment.locationZ
    );
    const value = parseModelPositionLength(
      state.draft.position[axis],
      props.formattingSettings,
      getVectorAxis(current, axis)
    );
    commitPosition(setVectorAxis(current, axis, value));
    notifyAlignmentApplied();
  };

  const commitMoveAxis = (axis: Axis): void => {
    const current = state.move;
    const value = parseModelPositionLength(
      state.draft.move[axis],
      props.formattingSettings,
      getVectorAxis(current, axis)
    );
    applyMove(setVectorAxis(current, axis, value));
    notifyAlignmentApplied();
  };

  const commitRotationAxis = (axis: Axis): void => {
    const current = state.rotation;
    const value = parseModelPositionAngle(
      state.draft.rotation[axis],
      props.formattingSettings,
      getVectorAxis(current, axis)
    );
    const nextRotation = setVectorAxis(current, axis, value);
    applyRotation(nextRotation.x, nextRotation.y, nextRotation.z);
    notifyAlignmentApplied();
  };

  const commitScale = (): void => {
    const parsedScale = Number.parseFloat(state.draft.scale.replace(',', '.'));
    const nextScale =
      Number.isNaN(parsedScale) || parsedScale <= 0
        ? state.scalePercent
        : parsedScale;

    setState('scalePercent', nextScale);
    setState('draft', 'scale', nextScale.toString());
    setState('alignment', 'scale', nextScale / PERCENT_FACTOR);
    notifyAlignmentApplied();
  };

  const commitDistance = (): void => {
    const nextDistance = parseModelPositionLength(
      state.draft.move.distance,
      props.formattingSettings,
      magnitude(state.move)
    );
    setState(
      'draft',
      'move',
      'distance',
      formatModelPositionLength(nextDistance, props.formattingSettings)
    );

    const currentMove = state.move;
    let nextMove = vector3(nextDistance, 0, 0);
    if (currentMove.x !== 0 || currentMove.y !== 0 || currentMove.z !== 0) {
      nextMove = scaleVector(normalize(currentMove), nextDistance);
    }

    applyMove(nextMove);
    notifyAlignmentApplied();
  };

  createEffect(
    on(
      () => props.alignment,
      (nextAlignment: TcModelAlignment) => {
        const alignment = { ...nextAlignment };

        const initial = vector3(
          alignment.locationX,
          alignment.locationY,
          alignment.locationZ
        );

        // Position
        const zeroMove = createZeroVector();
        setState({
          alignment: alignment,
          initialPosition: initial,
          move: zeroMove,
        });
        setVectorDraft(setState, 'move', zeroMove, (value) =>
          formatModelPositionLength(value, props.formattingSettings)
        );
        setState(
          'draft',
          'move',
          'distance',
          formatModelPositionLength(0, props.formattingSettings)
        );

        commitPosition(initial);

        // Scale
        const nextScale = alignment.scale * PERCENT_FACTOR;
        setState('scalePercent', nextScale);
        setState('draft', 'scale', nextScale.toString());

        // Rotation
        const rotation = convertConnectModelAlignmentToViewerEuler(alignment);
        applyRotation(rotation.x, rotation.y, rotation.z);

        setInitialAlignment({ ...state.alignment });
      }
    )
  );

  return (
    <div data-testid="position-settings" class="flex flex-col h-full">
      <div class="flex flex-col flex-1 overflow-y-auto">
        <div class="flex flex-col p-3 gap-4">
          <div class="h5">{t('shared.ui.modelPosition.positionTitle')}</div>
          <div class="flex gap-6">
            <PositionInput
              name="position-x"
              label={t('shared.ui.modelPosition.positionX')}
              value={state.draft.position.x}
              onInput={(value) => setState('draft', 'position', 'x', value)}
              onCommit={() => commitPositionAxis('x')}
            />
            <PositionInput
              name="position-y"
              label={t('shared.ui.modelPosition.positionY')}
              value={state.draft.position.y}
              onInput={(value) => setState('draft', 'position', 'y', value)}
              onCommit={() => commitPositionAxis('y')}
            />
          </div>
          <PositionInput
            name="position-z"
            label={t('shared.ui.modelPosition.positionZ')}
            value={state.draft.position.z}
            onInput={(value) => setState('draft', 'position', 'z', value)}
            onCommit={() => commitPositionAxis('z')}
          />
          <PositionInput
            name="scale"
            label={t('shared.ui.modelPosition.scale')}
            value={state.draft.scale}
            onInput={(value) => setState('draft', 'scale', value)}
            onCommit={commitScale}
          />
        </div>

        <div class="flex flex-col mt-2 p-3 gap-4 border-t border-trimble-gray-300">
          <div class="h5">{t('shared.ui.modelPosition.rotationTitle')}</div>
          <div class="flex gap-6">
            <PositionInput
              name="rotation-x"
              label={t('shared.ui.modelPosition.rotationX')}
              value={state.draft.rotation.x}
              onInput={(value) => setState('draft', 'rotation', 'x', value)}
              onCommit={() => commitRotationAxis('x')}
            />
            <PositionInput
              name="rotation-y"
              label={t('shared.ui.modelPosition.rotationY')}
              value={state.draft.rotation.y}
              onInput={(value) => setState('draft', 'rotation', 'y', value)}
              onCommit={() => commitRotationAxis('y')}
            />
            <PositionInput
              name="rotation-z"
              label={t('shared.ui.modelPosition.rotationZ')}
              value={state.draft.rotation.z}
              onInput={(value) => setState('draft', 'rotation', 'z', value)}
              onCommit={() => commitRotationAxis('z')}
            />
          </div>
        </div>

        <div class="flex flex-col mt-2 p-3 gap-4 border-t border-trimble-gray-300">
          <div class="h5">{t('shared.ui.modelPosition.moveTitle')}</div>
          <PositionInput
            name="move-distance"
            label={t('shared.ui.modelPosition.moveDistance')}
            value={state.draft.move.distance}
            onInput={(value) => setState('draft', 'move', 'distance', value)}
            onCommit={commitDistance}
          />
          <PositionInput
            name="move-z"
            label={t('shared.ui.modelPosition.moveZ')}
            value={state.draft.move.z}
            onInput={(value) => setState('draft', 'move', 'z', value)}
            onCommit={() => commitMoveAxis('z')}
          />
          <div class="flex gap-6">
            <PositionInput
              name="move-x"
              label={t('shared.ui.modelPosition.moveX')}
              value={state.draft.move.x}
              onInput={(value) => setState('draft', 'move', 'x', value)}
              onCommit={() => commitMoveAxis('x')}
            />
            <PositionInput
              name="move-y"
              label={t('shared.ui.modelPosition.moveY')}
              value={state.draft.move.y}
              onInput={(value) => setState('draft', 'move', 'y', value)}
              onCommit={() => commitMoveAxis('y')}
            />
          </div>
        </div>
      </div>
      <div class="flex justify-end gap-2 px-3 py-3 border-t border-trimble-gray-300">
        <Button
          color={BtnClasses.LINK_SECONDARY}
          content={t('general.cancel')}
          onClick={() => props.onCancel()}
          disabled={props.isSaving}
          data-cy="model-position-cancel-button"
        />
        <Button
          color={BtnClasses.PRIMARY}
          content={t('general.save')}
          icon={props.isSaving ? 'tc-icon-spinner-light' : undefined}
          onClick={() => void props.onSave({ ...state.alignment })}
          disabled={props.isSaving}
          data-cy="model-position-save-button"
        />
      </div>
    </div>
  );
};
